/**
 * Small board overview (Excalidraw has none): every element's box, the
 * current viewport, click or drag to move the view there. Toggled with M.
 */
import { CaptureUpdateAction } from '@excalidraw/excalidraw';
import type { ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types';
import { useEffect, useRef } from 'react';

const W = 220;
const H = 150;
const PAD = 8;

export function Minimap({ xapi, onClose }: { xapi: ExcalidrawImperativeAPI; onClose(): void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const view = useRef<{ s: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    let frame = 0;
    const draw = () => {
      frame = 0;
      const c = canvas.current;
      const ctx = c?.getContext('2d');
      if (!c || !ctx) return;
      const st = xapi.getAppState();
      const z = st.zoom.value;
      const vp = { x: -st.scrollX, y: -st.scrollY, w: st.width / z, h: st.height / z };
      const els = xapi.getSceneElements().filter((e) => !e.isDeleted && !(e.type === 'text' && e.containerId));
      let x1 = vp.x, y1 = vp.y, x2 = vp.x + vp.w, y2 = vp.y + vp.h; // prettier-ignore
      for (const e of els) {
        x1 = Math.min(x1, e.x);
        y1 = Math.min(y1, e.y);
        x2 = Math.max(x2, e.x + e.width);
        y2 = Math.max(y2, e.y + e.height);
      }
      const s = Math.min((W - PAD * 2) / (x2 - x1 || 1), (H - PAD * 2) / (y2 - y1 || 1));
      const ox = PAD - x1 * s + ((W - PAD * 2) - (x2 - x1) * s) / 2;
      const oy = PAD - y1 * s + ((H - PAD * 2) - (y2 - y1) * s) / 2;
      view.current = { s, ox, oy };
      const dpr = window.devicePixelRatio || 1;
      c.width = W * dpr;
      c.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      for (const e of els) {
        const bg = e.backgroundColor && e.backgroundColor !== 'transparent' ? e.backgroundColor : null;
        ctx.fillStyle = bg ?? (e.type === 'frame' ? 'rgba(0,0,0,0.04)' : 'rgba(80,90,110,0.35)');
        ctx.fillRect(e.x * s + ox, e.y * s + oy, Math.max(1, e.width * s), Math.max(1, e.height * s));
      }
      ctx.strokeStyle = '#4262ff';
      ctx.lineWidth = 1.5;
      ctx.strokeRect(vp.x * s + ox, vp.y * s + oy, vp.w * s, vp.h * s);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(draw);
    };
    draw();
    const off = xapi.onChange(schedule);
    return () => {
      off();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [xapi]);

  const moveTo = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const v = view.current;
    if (!v) return;
    const r = e.currentTarget.getBoundingClientRect();
    const sx = (e.clientX - r.left - v.ox) / v.s;
    const sy = (e.clientY - r.top - v.oy) / v.s;
    const st = xapi.getAppState();
    const z = st.zoom.value;
    xapi.updateScene({ appState: { scrollX: st.width / 2 / z - sx, scrollY: st.height / 2 / z - sy }, captureUpdate: CaptureUpdateAction.NEVER });
  };

  return (
    <div className="xb-minimap" data-testid="minimap">
      <canvas
        ref={canvas}
        style={{ width: W, height: H }}
        aria-label="Board overview — click to move the view"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          moveTo(e);
        }}
        onPointerMove={(e) => e.buttons === 1 && moveTo(e)}
      />
      <button className="xb-minimap-x" onClick={onClose} aria-label="Hide minimap" title="Hide minimap (M)">
        ×
      </button>
    </div>
  );
}
